/**
 * Environment-aware logging utility
 * 
 * Prevents sensitive data (session IDs, tokens, API responses, audio data)
 * from being logged in production environments where they could be exposed
 * in browser dev tools or crash reporting services.
 * 
 * Usage:
 *   import { devLog, devWarn, devError } from '@/lib/devLog';
 *   devLog('[Socket] Session started:', sessionId);
 *   devWarn('[VoiceSocket] Connection issue:', reason);
 * 
 * Note: devError always logs (even in production) since errors are critical
 * for debugging production issues and typically don't contain sensitive data.
 */

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Development-only console.log
 * Silently ignored in production
 * @param {...any} args - Arguments to log
 */
export const devLog = (...args) => {
  if (!isProduction) {
    console.log(...args);
  }
};

/**
 * Development-only console.warn
 * Silently ignored in production
 * @param {...any} args - Arguments to log
 */
export const devWarn = (...args) => {
  if (!isProduction) {
    console.warn(...args);
  }
};

/**
 * Console.error that always logs (even in production)
 * Errors are critical for debugging and typically safe to log
 * @param {...any} args - Arguments to log
 */
export const devError = (...args) => {
  console.error(...args);
};

/**
 * Development-only console.info
 * Silently ignored in production
 * @param {...any} args - Arguments to log
 */
export const devInfo = (...args) => {
  if (!isProduction) {
    console.info(...args);
  }
};

/**
 * Development-only console.debug
 * Silently ignored in production
 * @param {...any} args - Arguments to log
 */
export const devDebug = (...args) => {
  if (!isProduction) {
    console.debug(...args);
  }
};

export default {
  log: devLog,
  warn: devWarn,
  error: devError,
  info: devInfo,
  debug: devDebug,
};
